import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DEFAULT_LOW_STOCK_THRESHOLD as DOMAIN_DEFAULT_LOW_STOCK_THRESHOLD } from '@/supabase/inventory/types/inventory';
import type { CreateInventoryItemInput } from '@/supabase/inventory/types/inventory';
import { parseNonNegativeInt } from '@/components/inventory/parseNonNegativeInt';

export interface NewItemModalHandle {
  open: () => void;
}

interface NewItemModalContainerProps {
  createItem: (input: CreateInventoryItemInput) => Promise<boolean>;
  isSubmitting: boolean;
}

const DEFAULT_INITIAL_STOCK = '0';
const DEFAULT_PRICE = '0';
// 도메인 상수(types/inventory)에서 파생 — 미매칭 재고 등록 다이얼로그와 값 드리프트 불가
// (codexloop R1 #5 수용). 입력 초기값이라 문자열로 변환해 보관.
const DEFAULT_LOW_STOCK_THRESHOLD = String(DOMAIN_DEFAULT_LOW_STOCK_THRESHOLD);

/**
 * 신규 상품 등록 모달을 Home/Inventory 페이지에서 분리해 자체 관리한다.
 * - 열림 상태/폼 입력값을 내부 state로 보유. `open()`만 imperative handle로 노출 →
 *   부모는 ref 호출만 하므로 모달 오픈 시 부모가 재렌더되지 않는다.
 *
 * ⚠️ React.memo로 감싸지 말 것. isSubmitting은 제출 중 버튼 비활성화(중복 제출 가드)를
 *    실시간으로 반영해야 하는 부모 live prop이다. 지금은 매 렌더 원시값이라 memo를 씌워도
 *    정상 반영되지만, 다른 모달 컨테이너 선례(CombinedShippingModalContainer)와 동일하게
 *    "open-state를 내부에 격리한 컨테이너는 항상 memo 금지"로 통일해 향후 prop 안정화
 *    리팩터가 조용히 재렌더를 생략시키는 사고를 막는다.
 */
export const NewItemModalContainer = forwardRef<NewItemModalHandle, NewItemModalContainerProps>(
  ({ createItem, isSubmitting }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [name, setName] = useState('');
    const [initialStock, setInitialStock] = useState(DEFAULT_INITIAL_STOCK);
    const [price, setPrice] = useState(DEFAULT_PRICE);
    const [lowStockThreshold, setLowStockThreshold] = useState(DEFAULT_LOW_STOCK_THRESHOLD);
    // 진행 중이던 제출(await)이 끝나기 전에 모달이 닫혔다가 다른 대상으로 재오픈되면,
    // 그 응답이 새 세션의 폼/오픈 상태를 건드리지 않도록 세대를 구분한다.
    const sessionRef = useRef(0);

    const resetForm = useCallback(() => {
      setName('');
      setInitialStock(DEFAULT_INITIAL_STOCK);
      setPrice(DEFAULT_PRICE);
      setLowStockThreshold(DEFAULT_LOW_STOCK_THRESHOLD);
    }, []);

    const open = useCallback(() => {
      sessionRef.current += 1;
      resetForm();
      setIsOpen(true);
    }, [resetForm]);

    useImperativeHandle(ref, () => ({ open }), [open]);

    const handleOpenChange = useCallback(
      (next: boolean) => {
        if (!next) {
          sessionRef.current += 1;
          resetForm();
        }
        setIsOpen(next);
      },
      [resetForm],
    );

    const handleSubmit = useCallback(async () => {
      const trimmedName = name.trim();
      const parsedStock = parseNonNegativeInt(initialStock);
      const parsedPrice = parseNonNegativeInt(price);
      const parsedThreshold = parseNonNegativeInt(lowStockThreshold);
      if (!trimmedName || parsedStock === null || parsedPrice === null || parsedThreshold === null) return;

      const session = sessionRef.current;
      const success = await createItem({
        name: trimmedName,
        initialStock: parsedStock,
        price: parsedPrice,
        lowStockThreshold: parsedThreshold,
      });
      if (session !== sessionRef.current) return;
      if (success) {
        resetForm();
        setIsOpen(false);
      }
    }, [name, initialStock, price, lowStockThreshold, createItem, resetForm]);

    const isNameValid = name.trim().length > 0;
    const isStockValid = parseNonNegativeInt(initialStock) !== null;
    const isPriceValid = parseNonNegativeInt(price) !== null;
    const isThresholdValid = parseNonNegativeInt(lowStockThreshold) !== null;
    const isFormValid = isNameValid && isStockValid && isPriceValid && isThresholdValid;

    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>신규 상품 등록</DialogTitle>
            <DialogDescription className="break-keep">
              같은 이름의 삭제된 상품이 있으면 복원되며, 재고는 초기 재고 값으로 맞춰집니다.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="inventory-new-item-name">상품명</Label>
              <Input
                id="inventory-new-item-name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="예: 반팔 티셔츠 화이트"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="inventory-new-item-stock">초기 재고</Label>
                <Input
                  id="inventory-new-item-stock"
                  type="number"
                  min={0}
                  step={1}
                  value={initialStock}
                  onChange={(e) => setInitialStock(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inventory-new-item-price">판매가</Label>
                <Input
                  id="inventory-new-item-price"
                  type="number"
                  min={0}
                  step={1}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inventory-new-item-threshold">부족 알림 기준</Label>
                <Input
                  id="inventory-new-item-threshold"
                  type="number"
                  min={0}
                  step={1}
                  value={lowStockThreshold}
                  onChange={(e) => setLowStockThreshold(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
              취소
            </Button>
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800"
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid}
            >
              등록
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

NewItemModalContainer.displayName = 'NewItemModalContainer';
